import { useApolloClient } from "@apollo/client";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { CancelButton, SubmitButton } from "Components/Button";
import { CollapsibleSection } from "Components/CollapsibleSection";
import { Horizontal } from "Components/Horizontal";
import { SmallIconButton } from "Components/Icon";
import { InformationRow } from "Components/InformationRows";
import { Modal } from "Components/Modal";
import { useModalFormState } from "Components/ModalState";
import { Section } from "Components/Section";
import {
  updateCaseNumberMutation,
  updateCaseStatusMutation,
} from "generated/graphql";
import { DateInput } from "Inputs/DateInput";
import { useFormWithSchema } from "Inputs/FormTools";
import { Textbox } from "Inputs/Textbox";
import { YupDefined } from "Inputs/Tools";
import React, { useState } from "react";
import { hasValue, formatShortUIDate, Assert, hv } from "Tools";
import * as Yup from "yup";
import { Application } from "./Types";

function UpdateCaseStatusControlWithModal(props: {
  app: Application;
  case: NonNullable<Application["case"]>;
}) {
  const apolloClient = useApolloClient();
  const [isVisible, setVisible] = useState(false);
  const form = useFormWithSchema({
    initialValues: {
      caseClosedOn: new Date(),
    },
    onSubmit: async values => {
      await apolloClient.mutate({
        mutation: updateCaseStatusMutation,
        variables: {
          input: {
            securityKey: props.app.securityKey,
            closedOn: values.caseClosedOn,
          },
        },
      });
      setVisible(false);
    },
    validationSchema: Yup.object({
      caseClosedOn: DateInput.getDefaultValidator()
        .label("Case Closed On")
        .required(),
    }),
  });
  const onCancel = form.isSubmitting ? () => null : () => setVisible(false);
  return (
    <>
      <Modal
        title="Update Case Status"
        isVisible={isVisible}
        buttonRow={
          <>
            <CancelButton disabled={form.isSubmitting} onClick={onCancel} />
            <SubmitButton
              isSubmitting={form.isSubmitting}
              onClick={form.submitForm}
            />
          </>
        }
        onCancel={onCancel}
      >
        <Section title={props.case.caseName} className="narrowFieldBodies1">
          <div style={{ textAlign: "center", marginBottom: "1em" }}>
            <em>Please select the date that the case was closed.</em>
          </div>
          <Horizontal {...form.field.caseClosedOn.getLabelFromSchema()}>
            <DateInput field={form.field.caseClosedOn} />
          </Horizontal>
        </Section>
      </Modal>
      <SmallIconButton icon={faEdit} onClick={() => setVisible(true)} />
    </>
  );
}
export function CaseCollapsible(props: {
  app: Application;
  case: NonNullable<Application["case"]>;
}) {
  return (
    <CollapsibleSection
      title={<>Case: {props.case.caseName}</>}
      theme={{ header: { className: "is-secondary" } }}
    >
      <InformationRow label="Jurisdiction">
        {props.app.jurisdiction}
      </InformationRow>
      {props.app.jurisdiction?.toUpperCase() === "FEDERAL" && (
        <InformationRow label="Federal Court State">
          {props.case.federalCourtState}
        </InformationRow>
      )}
      <InformationRow label="Complaint Document">
        {props.case.willEmailComplaintSeparately ? "To Be Emailed" : "Uploaded"}
      </InformationRow>
      <InformationRow label="Case Type" shouldTopAlign>
        {props.case.caseTypes.map(caseType => (
          <div key={caseType}>{caseType}</div>
        ))}
      </InformationRow>
      {!hasValue(props.case.closedOn) ? (
        <InformationRow label="Case Status">
          Open <UpdateCaseStatusControlWithModal {...props} />
        </InformationRow>
      ) : (
        <>
          <InformationRow label="Case Status">Closed</InformationRow>
          <InformationRow label="Closed On">
            {formatShortUIDate(props.case.closedOn)}
          </InformationRow>
        </>
      )}
      <InformationRow label="Case Number">
        {props.case.caseNumber ?? "TBD"}{" "}
        <UpdateCaseNumberControlWithModal app={props.app} />
      </InformationRow>
      {props.app.case?.lastUpdatedByAdmin && (
        <InformationRow label="Case Updated By">
          {props.app.case.lastUpdatedByAdmin.fullName}
        </InformationRow>
      )}
    </CollapsibleSection>
  );
}

function UpdateCaseNumberControlWithModal({ app }: { app: Application }) {
  const apolloClient = useApolloClient();
  Assert.hard(hv(app.case), `Null case for ${app.id}`);
  const form = useFormWithSchema({
    initialValues: { caseNumber: app.case?.caseNumber ?? null },
    onSubmit: async values => {
      await apolloClient.mutate({
        mutation: updateCaseNumberMutation,
        variables: {
          input: {
            securityKey: app.securityKey,
            caseNumber: values.caseNumber,
          },
        },
      });
    },
    validationSchema: Yup.object({
      caseNumber: YupDefined.string().label("Case Number").max(50).nullable(),
    }),
  });
  const modalState = useModalFormState({ form });
  const onCancel = form.isSubmitting ? () => null : () => modalState.close();
  return (
    <>
      <Modal
        title="Update Case Number"
        {...modalState}
        buttonRow={
          <>
            <CancelButton disabled={form.isSubmitting} onClick={onCancel} />
            <SubmitButton
              disabled={!form.dirty}
              isSubmitting={form.isSubmitting}
              onClick={() => modalState.submitFormAndClose()}
            />
          </>
        }
        onCancel={onCancel}
      >
        <Section title={app.case.caseName} className="narrowFieldBodies1">
          <Horizontal {...form.field.caseNumber.getLabelFromSchema()}>
            <Textbox
              inputProps={{ autoComplete: "off" }}
              fieldProps={form.field.caseNumber}
            />
          </Horizontal>
          <p style={{ textAlign: "center" }}>
            <i>Leave empty for no case number.</i>
          </p>
        </Section>
      </Modal>
      <SmallIconButton icon={faEdit} onClick={() => modalState.open()} />
    </>
  );
}
